import { DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { TextInputFormik } from '@strata/react-inputs';
import { StatusIcon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { FieldArrayRenderProps, useFormikContext } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { EligibilityDialog } from 'components/admin/automations/dialogs/EligibilityDialog';
import {
    AutomationRuleCriteria,
    AutomationRuleCriterion,
    AutomationRuleFormContext,
    findAutomationRuleCriterionValue,
} from 'services/AutomationRuleHelpers';
import { handleError } from 'services/Error';
import { useLazyGetBenefitCheckQuery } from 'store/api/scopeAssessorApi';

interface PatientCriteriaProps {
    arrayHelpers: FieldArrayRenderProps;
    index: number;
    disabled: boolean;
}

export const PatientCriteria = ({ arrayHelpers, index, disabled }: PatientCriteriaProps) => {
    const formik = useFormikContext<AutomationRuleFormContext>();

    const criteria = formik.values[arrayHelpers.name as 'criteria'];
    const patientCriteria = criteria[index] as AutomationRuleCriterion<
        typeof AutomationRuleCriteria.PATIENT
    >;
    const patientCriteriaValues = patientCriteria?.value ?? {};

    const [checkingBenefits, setCheckingBenefits] = useState(false);
    const [showEligibilityDialog, setShowEligibilityDialog] = useState(false);
    const [getBenefitCheck, { data: benefitInfo }] = useLazyGetBenefitCheckQuery();
    const checkBenefits = async () => {
        setCheckingBenefits(true);
        try {
            const segment = findAutomationRuleCriterionValue(
                criteria,
                AutomationRuleCriteria.PAYER,
            );
            if (segment) {
                await getBenefitCheck({
                    payerId: segment.payerId,
                    segmentId: segment.id,
                    memberId: patientCriteriaValues.id,
                    dateOfBirth: patientCriteriaValues.dob,
                    firstName: patientCriteriaValues.name,
                });
                setShowEligibilityDialog(true);
            }
        } catch (err) {
            handleError('Error processing benefit check.', err as Error);
        } finally {
            setCheckingBenefits(false);
        }
    };

    const updatePatientValues = (newValues: {
        id?: string;
        name?: string;
        dob?: dayjs.ConfigType;
        benefitCheckSuccess?: boolean;
    }) =>
        arrayHelpers.replace(index, {
            ...patientCriteria,
            value: {
                ...patientCriteriaValues,
                ...newValues,
            },
        });

    return (
        <div className="sds_automation-template__patient-section">
            <FullscreenLoading
                isShowing={checkingBenefits}
                loadingText="Checking patient benefits and eligibility..."
            />
            <EligibilityDialog
                isOpen={showEligibilityDialog}
                benefitInfo={benefitInfo}
                onSecondaryButtonClick={() => setShowEligibilityDialog(false)}
                onPrimaryButtonClick={() => {
                    setShowEligibilityDialog(false);
                    updatePatientValues({
                        benefitCheckSuccess: true,
                        name: benefitInfo?.patient_first_name,
                    });
                }}
            />
            <TextInputFormik
                id={`${arrayHelpers.name}.${index}.value.name`}
                placeholderText="Patient First Name"
                disabled={disabled}
                required
                verticalSpacingSmall
                onChange={(e) =>
                    updatePatientValues({
                        name: e.target.value,
                        benefitCheckSuccess: false,
                    })
                }
            />
            <TextInputFormik
                id={`${arrayHelpers.name}.${index}.value.id`}
                placeholderText="Cardholder ID"
                disabled={disabled}
                required
                verticalSpacingSmall
                onChange={(e) =>
                    updatePatientValues({
                        id: e.target.value,
                        benefitCheckSuccess: false,
                    })
                }
            />
            <DateInputFormik
                id={`${arrayHelpers.name}.${index}.value.dob`}
                placeholderText="DOB (MM/DD/YYYY)"
                disabled={disabled}
                required
                verticalSpacingSmall
                onChange={(newValue) =>
                    updatePatientValues({
                        dob: newValue,
                        benefitCheckSuccess: false,
                    })
                }
            />
            {patientCriteriaValues.benefitCheckSuccess && (
                <div className="sds_automation-template__eligible-patient">
                    <StatusIcon
                        status={StatusIcon.Statuses.GOOD}
                        className="padding__right--small"
                        size={14}
                    />
                    <span>Patient is eligible.</span>
                </div>
            )}
            <Button
                label="Check Patient Benefits"
                intent={Button.Intent.PRIMARY}
                disabled={
                    patientCriteriaValues.benefitCheckSuccess ||
                    !(
                        findAutomationRuleCriterionValue(criteria, AutomationRuleCriteria.PAYER) &&
                        patientCriteriaValues.name &&
                        patientCriteriaValues.id &&
                        patientCriteriaValues.dob
                    )
                }
                onClick={() => {
                    (() => checkBenefits())();
                }}
            />
        </div>
    );
};

PatientCriteria.validationSchema = (schema: Yup.AnyObjectSchema) =>
    schema.shape({
        name: Yup.string().required(validationStrings.requiredField.errorMessage),
        id: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            .min(5, validationStrings.minCharacter.lambda(5))
            .max(12, validationStrings.maxCharacter.lambda(12)),
        dob: DateInputFormik.validator()
            .required(validationStrings.requiredField.errorMessage)
            .max(dayjs().endOf('day'), 'Date of birth cannot be in the future.'),
        benefitCheckSuccess: Yup.bool().equals([true]),
    });

PatientCriteria.submitValues = (value?: {
    name?: string;
    id?: string;
    dob?: dayjs.ConfigType;
}) => ({
    memberFirstName: value?.name,
    memberId: value?.id,
    memberDOB: formatDate(value?.dob, DateFormats.HYPHEN_DATE),
});
